import * as React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Table, Checkbox, Tooltip, Input, Card, Icon } from '@beehive/components';
import { InputPassword } from 'components';
import { SUPPLIER_EMPLOYEES_KEYS_MAPPING } from '../utils';
import { defaultColumnRender } from 'utils/table';
import { showAlert } from 'utils';
import messages from 'messages/CompanyManagement';

const COLUMN_KEYS = ['user_login', 'status', 'user_name', 'user_email', 'user_popo', 'user_password'];

export default class SupplierEmployees extends React.Component<any, any> {

  public static contextTypes = {
    intl: PropTypes.object,
  };

  private tableRef: React.RefObject<typeof Table>;

  constructor(props: any) {
    super(props);
    this.tableRef = React.createRef();
  }

  public shouldComponentUpdate(nextProps: any, nextState: any) {
    if (this.props.showError !== nextProps.showError) {
      return true;
    }
    const pickKeys = ['detailError.messages', 'supplier_employees', 'loading', 'loaded'];
    if (_.isEqual(_.pick(this.props.data, pickKeys), _.pick(nextProps.data, pickKeys))) {
      return false;
    }
    return true;
  }

  public isFormValid = (): boolean => {
    const { intl } = this.context;
    if (this.tableRef.current) {
      return this.tableRef.current.valid((invalidRows: any) => {
        if (invalidRows && invalidRows.length) {
          showAlert({
            type: 'error',
            message: intl.formatMessage(messages.supplierEmployeesSaveAlert, { rowsCount: invalidRows.length }),
          });
        }
        return true;
      });
    }
    return true;
  }

  private handleChange = (options: any = {}) => {
    const { onChange, data } = this.props;
    const { index, key, value } = options;
    const supplierEmployees = _.cloneDeep(_.get(this, 'props.data.supplier_employees', []));
    _.set(supplierEmployees, `${index}.${key}`, value);
    onChange({
      ...data,
      supplier_employees: supplierEmployees,
    });
  }

  private getRowClassName = (r: any) => {
    if (r.status === 'locked') {
      return 'row-locked';
    } else {
      return '';
    }
  }

  private getColumnsRender = () => {
    const { intl } = this.context;
    const columnsRender = COLUMN_KEYS.reduce((acc: any, cur: string) => {
      const editProps = _.get(SUPPLIER_EMPLOYEES_KEYS_MAPPING, `${cur}.editProps`, {});
      const { componentClass } = editProps;
      if (componentClass) {
        acc[cur] = (v: any, r: any, i: number) => {
          if (v && v.props) {
            return v;
          } else {
            switch (componentClass) {
              case 'input': {
                return (
                  <Input
                    size="large"
                    value={v}
                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) => this.handleChange({
                      value: evt.target.value,
                      row: r,
                      index: i,
                      key: cur,
                    })}
                    placeholder={editProps.placeholder && intl.formatMessage(messages[editProps.placeholder])}
                    type={editProps.type}
                    maxLength={editProps.maxLength}
                    disabled={!!editProps.disabled}
                  />
                );
              }
              case 'inputPassword': {
                return (
                  <InputPassword
                    size="large"
                    value={v}
                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) => this.handleChange({
                      value: evt.target.value,
                      row: r,
                      index: i,
                      key: cur,
                    })}
                    placeholder={editProps.placeholder && intl.formatMessage(messages[editProps.placeholder])}
                    type={editProps.type}
                    maxLength={editProps.maxLength}
                  />
                );
              }
              case 'checkbox': {
                return (
                  <Checkbox
                    checked={v === 'active'}
                    onChange={(e: any) => {
                      const val = e.target.checked ? 'active' : 'locked';
                      this.handleChange({
                        value: val,
                        row: r,
                        index: i,
                        key: cur,
                      });
                    }}
                  >
                    {intl.formatMessage(messages.status, { status: 'active' })}
                  </Checkbox>
                );
              }
              default:
                break;
            }
          }
          return null;
        };
      }
      return acc;
    }, {});
    return columnsRender;
  }

  private getColumnsTitle = () => {
    const { intl } = this.context;
    const columnsTitle = COLUMN_KEYS.reduce((acc: any, cur: string) => {
      const required = _.get(SUPPLIER_EMPLOYEES_KEYS_MAPPING, `${cur}.editProps.required`);
      let title = intl.formatMessage(messages[SUPPLIER_EMPLOYEES_KEYS_MAPPING[cur].name], { status: undefined });
      let tooltip = SUPPLIER_EMPLOYEES_KEYS_MAPPING[cur].tooltip || null;
      if (tooltip) {
        tooltip = intl.formatMessage(messages[tooltip]);
      }
      if (tooltip) {
        title = (
          <span>
            {title}
            <Tooltip title={tooltip}>
              <Icon name="question-circle-o" />
            </Tooltip>
          </span>
        );
      }
      acc[cur] = required ? <span className="required-label">{title}</span> : title;
      return acc;
    }, {});
    return columnsTitle;
  }

  private getColumnsWidth = () => {
    return {
      user_login: 160,
      status: 104,
    };
  }

  private getColumns = () => {
    const { intl } = this.context;
    const columnsTitle = this.getColumnsTitle();
    const columnsRender = this.getColumnsRender();
    const columnsWidth = this.getColumnsWidth();
    return COLUMN_KEYS.map((key: string) => {
      const column = SUPPLIER_EMPLOYEES_KEYS_MAPPING[key];
      const columnProps = column.editProps || {};
      return {
        key,
        dataIndex: key,
        title: columnsTitle[key] || intl.formatMessage(messages[column.name]),
        render: columnsRender[key] || defaultColumnRender,
        width: columnsWidth[key] || 250,
        className: key,
        ...columnProps,
      };
    });
  }

  private getTableData = () => {
    const supplierEmployees = _.get(this, 'props.data.supplier_employees', []);
    return [...supplierEmployees];
  }

  public render() {
    const { intl } = this.context;
    const { data = {} } = this.props;
    const { loading } = data;
    const tableData = this.getTableData();
    const columns = this.getColumns();

    return (
      <Card
        titleNode={intl.formatMessage(messages.accountsInfo)}
        className="employees-info"
        loading={loading}
      >
        <Table
          data={tableData}
          columns={columns}
          rowKey={(row: any) => row.id}
          ref={this.tableRef}
          rowClassName={this.getRowClassName}
        />
      </Card>
    );
  }
}
